<div class="bk-page compose">
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <p class="bk-eyebrow">SMS</p>
      <h1 id="compose-title" class="bk-page__title" tabindex="-1">
        {{ currentView === "writing" ? "New message" : "Review and send" }}
      </h1>
      <p class="bk-page__sub">
        @if (currentView === "writing") {
          Write the message, choose who gets it, and see what it costs before anything goes out.
        } @else {
          Check who gets it, when, and what it costs. Nothing is sent until you confirm.
        }
      </p>
    </div>
    <!-- On review, the action bar's "Back to editing" and the panel's "Edit" lead back. -->
    @if (currentView === "writing") {
      <div class="bk-page__actions">
        <button type="button" class="bk-btn bk-btn--ghost" (click)="handleBack()">
          <span class="bk-sym" aria-hidden="true">arrow_back</span>
          Messages
        </button>
      </div>
    }
  </header>

  @if (prefillNote) {
    <p class="compose__note" role="status">
      <span class="bk-sym" aria-hidden="true">link</span>
      <span>{{ prefillNote }}</span>
    </p>
  }

  @if (currentView === "writing") {
    <div class="compose__grid">
      <div class="compose__main">
        <create-message-contents
          [messageContentForm]="messageContentForm"
          [submitted]="submitted"
          [schedule]="schedule"
          [scheduleInPast]="scheduleInPast"
        >
          <section
            recipients
            class="bk-panel compose__panel"
            aria-labelledby="compose-recipients-title"
            [attr.data-problem]="submitted && !atleastOneRecipientSelected ? 'true' : null"
          >
            <create-message-select-recipient
              (openSelectContactsModalEvent)="openAddContactsModal()"
              (openSelectTagsModalEvent)="openAddTagsModal()"
              (openSelectSegmentModalEvent)="openAddSegmentsModal()"
              (openSelectOrganizationsModalEvent)="openAddOrganizationsModal()"
              (emitPhoneNumberAdded)="addPhoneNumber($event)"
              [contactsCount]="selectedContacts.length"
              [tagsCount]="selectedTags.length"
              [segmentsCount]="selectedSegments.length"
              [organizationsCount]="selectedOrganizations.length"
              [phoneNumberCount]="phoneNumbersCount"
              [csvFileCount]="localCSVService.getAllFormattedFileData.length"
              [missing]="submitted && !atleastOneRecipientSelected"
              (openCSVPhoneNumbersModalEvent)="openCsvUpload()"
            />

            <create-message-preview
              (openPhoneNumberModal)="openPhoneNumbersModal()"
              (openContactsSelectedModal)="openContactSelectedModal()"
              (openCSVSelectedFilesResultModal)="openCSVFilesModal()"
              (openTagsSelectedModal)="openTagSelectedModal()"
              (openSegmentsSelectedModal)="openSegmentSelectedModal()"
              (openOrganizationsSelectedModal)="openOrganizationSelectedModal()"
              [phoneNumbers]="phoneNumbers"
              (removePhoneNumber)="removePhoneNumber($event)"
              [selectedContacts]="selectedContacts"
              [selectedTags]="selectedTags"
              [selectedOrganizations]="selectedOrganizations"
              [selectedSegments]="selectedSegments"
              (removeTag)="removeTag($event)"
              (removeContact)="removeContact($event)"
              (removeOrganization)="removeOrganization($event)"
              (removeSegment)="removeSegment($event)"
            />
          </section>
        </create-message-contents>
      </div>

      <aside class="compose__aside" aria-label="Preview">
        <compose-aside
          [senderId]="messageContentForm.value.senderId || ''"
          [body]="messageContentForm.value.messageBody || ''"
          [parts]="bodyParts"
          [scheduled]="isScheduledSend"
          [schedule]="schedule"
          [hasRecipients]="atleastOneRecipientSelected"
          [audienceCount]="audienceCount"
          [countLoading]="audienceCountLoading"
          [countError]="audienceCountError"
          [countApprox]="audienceCountApprox"
          [breakdown]="audienceBreakdown"
          [estimatedSms]="estimatedSms"
          (retryCount)="refreshAudienceCount(true)"
        />
      </aside>
    </div>
  } @else {
    <preview-message-details
      (backToWritingEvent)="updateView('writing')"
      [senderID]="messageContentForm.value.senderId || ''"
      [messageBody]="messageContentForm.value.messageBody || ''"
      [messageSubject]="messageContentForm.value.messageSubject || ''"
      [phoneNumbers]="phoneNumbers"
      [selectedContacts]="selectedContacts"
      [selectedTags]="selectedTags"
      [selectedSegments]="selectedSegments"
      [selectedOrganizations]="selectedOrganizations"
      [recipientCount]="audienceCount"
      [isFetchingCount]="audienceCountLoading"
      [countError]="audienceCountError"
      [countApprox]="audienceCountApprox"
      [breakdown]="audienceBreakdown"
      [parts]="bodyParts"
      [estimatedSms]="estimatedSms"
      [scheduled]="isScheduledSend"
      [schedule]="schedule"
      (retryCount)="refreshAudienceCount(true)"
    />
  }

  <!-- Action bar: what is left to do, and the one next step -->
  <div class="compose__bar">
    <div class="compose__bar-inner">
      <p class="compose__status">
        <!-- Only problems are announced; the running tally changes with every keystroke. -->
        <span aria-live="polite">
          @if (barError) {
            <span class="compose__status-bad">
              <span class="bk-sym" aria-hidden="true">error</span>{{ barError }}
            </span>
          }
        </span>
        @if (!barError) {
          <span class="compose__status-text">
            @if (bodyParts.parts) {
              {{ bodyParts.parts === 1 ? "1 part" : bodyParts.parts + " parts" }}
            } @else {
              No message yet
            }
            <span class="compose__sep" aria-hidden="true">·</span>
            @if (!atleastOneRecipientSelected) {
              No recipients yet
            } @else if (audienceCount !== null) {
              {{ countApproxPrefix() }}{{ formatCount(audienceCount) }}
              {{ plural(audienceCount, "recipient", "recipients") }}
            } @else if (audienceCountError && !audienceCountLoading) {
              Recipients not counted
            } @else {
              Counting recipients
            }
            @if (estimatedSms !== null) {
              <span class="compose__sep" aria-hidden="true">·</span>
              about {{ formatCount(estimatedSms) }} SMS
            }
          </span>
        }
      </p>

      <div class="compose__actions">
        @if (currentView === "writing") {
          <button type="button" class="bk-btn bk-btn--ghost" (click)="handleCancel()">Cancel</button>
          <button type="button" class="bk-btn bk-btn--primary" (click)="goToReview()">
            Review message
            <span class="bk-sym compose__trail" aria-hidden="true">arrow_forward</span>
          </button>
        } @else {
          <button type="button" class="bk-btn bk-btn--ghost" (click)="updateView('writing')">
            Back to editing
          </button>
          <button
            type="button"
            class="bk-btn bk-btn--primary"
            [disabled]="broadcastMessageService.isLoading"
            [attr.aria-busy]="broadcastMessageService.isLoading"
            (click)="handleSubmit()"
          >
            <span class="bk-sym" aria-hidden="true">{{ isScheduledSend ? "schedule_send" : "send" }}</span>
            @if (broadcastMessageService.isLoading) {
              {{ isScheduledSend ? "Scheduling…" : "Sending…" }}
            } @else {
              {{ isScheduledSend ? "Schedule send" : "Send now" }}
            }
          </button>
        }
      </div>
    </div>
  </div>

  <!-- MODALS -->
  <!-- selected recipients: see and remove -->
  <message-phone-numbers-modal
    [open]="showPhoneNumbersModal"
    (close)="closePhoneNumbersModal()"
    [phoneNumbers]="phoneNumbers"
    (removePhoneNumber)="removePhoneNumber($event)"
  />
  <message-selected-organizations
    [open]="showOrganizationSelectedModal"
    (close)="closeOrganizationSelectedModal()"
    [selectedOrganizations]="selectedOrganizations"
    (removeOrganization)="removeOrganization($event)"
  />
  <message-selected-segments
    [open]="showSegmentSelectedModal"
    (close)="closeSegmentSelectedModal()"
    [selectedSegments]="selectedSegments"
    (removeSegment)="removeSegment($event)"
  />
  <message-selected-tags
    [open]="showTagSelectedModal"
    (close)="closeTagSelectedModal()"
    [selectedTags]="selectedTags"
    (removeTag)="removeTag($event)"
  />
  <message-selected-contacts
    [open]="showContactSelectedModal"
    (close)="closeContactSelectedModal()"
    [selectedContacts]="selectedContacts"
    (removeSelectedContacts)="removeSelectedContacts($event)"
    (removeAllSelectedContacts)="removeAllSelection()"
  />
  <message-selected-csv-results
    [open]="showCSVFilesModal"
    (close)="closeCSVFilesModal()"
  />

  <!-- recipient pickers -->
  <assign-contacts-to-group
    [open]="showAddContactsModal"
    title="Choose contacts"
    (close)="closeAddContactsModal()"
    (assignContacts)="addSelectedContacts($event)"
    [selectedContacts]="selectedContacts"
  />
  <add-organizations-modal
    [open]="showAddOrganizationsModal"
    (close)="closeAddOrganizationsModal()"
    (updateSelection)="addSelectedOrganizations($event)"
    [selection]="selectedOrganizations"
  />
  <add-tags-modal
    [open]="showAddTagsModal"
    (close)="closeAddTagsModal()"
    [selection]="selectedTags"
    (updateSelection)="addSelectedTags($event)"
  />
  <add-segments-modal
    [open]="showAddSegmentsModal"
    (close)="closeAddSegmentsModal()"
    [selection]="selectedSegments"
    (updateSelection)="addSelectedSegments($event)"
  />

  <!-- numbers from a file -->
  <app-csv-phone-numbers-upload
    [open]="openCsvUploadModal"
    (close)="closeCsvUpload()"
  />

  <!-- send with added numbers: save them first, or not -->
  <save-phone-numbers-component
    [showSavePhoneNumbersModal]="showSavePhoneNumbersModal"
    (closeSavePhoneNumbersModalEvent)="closeSavePhoneNumbersModal()"
    [messageDetails]="getMessageDetails"
  />
</div>
